import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { fmtD } from "@/lib/semanas";

const n5 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`);
const folio = (c) => (c || "").replace(/^PM-DC-SEPIC-GPDC-SPPCI-/, "");
const card = "bg-white rounded-2xl border border-slate-200/70 shadow-sm";

function Var({ v, pct }) {
  if (v == null) return <span className="text-slate-300">—</span>;
  const cls = v > 0 ? "text-red-600" : v < 0 ? "text-emerald-600" : "text-slate-400";
  const txt = pct ? `${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%` : `${v > 0 ? "+" : v < 0 ? "-" : ""}${n5(Math.abs(v))}`;
  return <span className={`font-semibold ${cls}`}>{txt}</span>;
}

function CircSelect({ pubs, value, onChange, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-96 bg-slate-50 border-slate-200 text-sm" data-testid={testid}><SelectValue placeholder="Circular" /></SelectTrigger>
      <SelectContent className="max-h-72">
        {pubs.map((p) => (
          <SelectItem key={p.id} value={p.id} data-testid={`${testid}-${folio(p.circular)}`}>
            Circular {folio(p.circular)} · {fmtD(p.vigencia_inicio)} – {fmtD(p.vigencia_fin)}{p.sustituida_por ? " · cancelada" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Comparación de dos circulares PEMEX por punto de entrega (referencia: Comparar periodos de Precios CNE).
export function PemexComparar() {
  const { data, isLoading } = useQuery({ queryKey: ["pemex", "publicaciones"], queryFn: async () => (await api.get("/control-semanal/pemex/publicaciones")).data });
  const pubs = data?.items || [];
  const vig = pubs.filter((p) => !p.sustituida_por);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const aId = a || vig[1]?.id || vig[0]?.id;
  const bId = b || vig[0]?.id;
  const { data: rows, isLoading: lr } = useQuery({
    queryKey: ["pemex", "comparar", aId, bId],
    queryFn: async () => (await api.get("/control-semanal/pemex/comparar", { params: { a: aId, b: bId } })).data.items,
    enabled: !!aId && !!bId,
  });
  const shown = useMemo(() => (rows || []).filter((r) => !onlyChanged || r.var !== 0), [rows, onlyChanged]);
  const stats = useMemo(() => {
    const v = (rows || []).filter((r) => r.var != null);
    return { sube: v.filter((r) => r.var > 0).length, baja: v.filter((r) => r.var < 0).length, igual: v.filter((r) => r.var === 0).length };
  }, [rows]);

  if (isLoading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  if (!pubs.length) return <div className={`${card} p-12 text-center text-slate-500`} data-testid="pemex-cmp-empty">Aún no hay circulares PEMEX importadas.</div>;
  const stat = (t, n, c, id) => <div className={`${card} p-4`}><p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{t}</p><p className={`text-2xl font-extrabold ${c}`} data-testid={id}>{n}</p></div>;
  return (
    <div className="space-y-4" data-testid="pemex-comparar">
      <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
        <CircSelect pubs={pubs} value={aId} onChange={setA} testid="pemex-cmp-a" />
        <ArrowRight className="w-4 h-4 text-slate-400" />
        <CircSelect pubs={pubs} value={bId} onChange={setB} testid="pemex-cmp-b" />
        <label className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
          <Switch checked={onlyChanged} onCheckedChange={setOnlyChanged} data-testid="pemex-cmp-only-changed" />Solo con variación
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="pemex-cmp-stats">
        {stat("Suben", stats.sube, "text-red-600", "pemex-cmp-sube")}
        {stat("Bajan", stats.baja, "text-emerald-600", "pemex-cmp-baja")}
        {stat("Sin cambio", stats.igual, "text-slate-700", "pemex-cmp-igual")}
      </div>
      <div className={`${card} overflow-hidden`}>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="pemex-cmp-table">
            <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Punto de entrega</th><th className="text-right px-4 py-2.5">Sin condiciones anterior</th><th className="text-right px-4 py-2.5">Sin condiciones actual</th>
                <th className="text-right px-4 py-2.5">Ajuste actual</th><th className="text-right px-4 py-2.5">Sin IVA anterior</th><th className="text-right px-4 py-2.5">Sin IVA actual</th>
                <th className="text-right px-4 py-2.5">Var. $/kg</th><th className="text-right px-4 py-2.5">Var. %</th></tr>
            </thead>
            <tbody>
              {lr ? <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="w-5 h-5 text-red-600 animate-spin inline" /></td></tr> :
                shown.length === 0 ? <tr><td colSpan={8} className="py-10 text-center text-slate-400" data-testid="pemex-cmp-sin-cambios">Sin variaciones entre las circulares seleccionadas.</td></tr> :
                shown.map((r) => (
                  <tr key={r.punto} className="border-t border-slate-100" data-testid={`pemex-cmp-row-${r.punto}`}>
                    <td className="px-4 py-2 font-semibold text-slate-800">{r.punto}{r.nota_iva_8 && <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">IVA 8%</span>}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{n5(r.base_a)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n5(r.base_b)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{n5(r.ajuste_b)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{n5(r.sin_iva_a)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-slate-900">{n5(r.sin_iva_b)}</td>
                    <td className="px-4 py-2 text-right tabular-nums"><Var v={r.var} /></td>
                    <td className="px-4 py-2 text-right tabular-nums"><Var v={r.var_pct} pct /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100" data-testid="pemex-cmp-count">{shown.length} puntos de entrega · variación sobre el precio sin IVA ($/kg)</div>
      </div>
    </div>
  );
}
